import { FileText, Image as ImageIcon, Lock } from 'lucide-react';
import type { PropertyFileResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatBytes, formatCurrency, formatDate } from '@/shared/lib/format';
import { AkteRowMenu } from './AkteRowMenu';
import { PROPERTY_DOCUMENT_KIND_SHORT } from './akteKindLabels';
import { buildFileMenuItems, type FileActions } from './akteFileActions';
import type { SelectionMods } from './useAkteSelection';

interface Props {
  file: PropertyFileResponse;
  selected: boolean;
  /** Auswahl-Modus aktiv (mind. eine Datei gewählt) → Checkbox bleibt sichtbar. */
  selectionActive: boolean;
  onClick: (mods: SelectionMods) => void;
  onToggleSelect: () => void;
  actions: FileActions;
  dragProps?: React.HTMLAttributes<HTMLLIElement> & { draggable?: boolean };
}

/** Datei-Zeile in der Content-Liste (Listen-Ansicht): Checkbox, Icon, Name, Kind, Meta, Größe, Menü. */
export function AkteContentFileRow({
  file,
  selected,
  selectionActive,
  onClick,
  onToggleSelect,
  actions,
  dragProps,
}: Props) {
  const isImage = file.contentType.startsWith('image/');
  const meta = buildMetaLine(file);
  const size = formatBytes(file.sizeBytes);

  return (
    <li
      {...dragProps}
      className={cn(
        'group flex items-center gap-1 rounded-lg border px-2 py-2.5 transition-colors sm:gap-3 sm:px-3.5 sm:py-3',
        selected ? 'border-sealwax bg-sealwax/5' : 'hover:bg-card-alt/50 border-transparent',
        dragProps?.draggable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      {/* 44-px-Griff um die Checkbox. Ohne Zeiger gibt es kein Hover: mobil bleibt sie sichtbar,
          sonst wäre die Mehrfachauswahl auf dem Telefon nicht erreichbar (§9). */}
      <label
        className={cn(
          'flex h-11 w-9 shrink-0 cursor-pointer items-center justify-center transition-opacity sm:w-7',
          selectionActive || selected
            ? 'opacity-100'
            : 'opacity-100 sm:opacity-0 sm:focus-within:opacity-100 sm:group-hover:opacity-100',
        )}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`${file.fileName} auswählen`}
          className="accent-sealwax h-4 w-4 cursor-pointer"
        />
      </label>
      <button
        type="button"
        onMouseDown={(e) => {
          if (e.shiftKey) e.preventDefault();
        }}
        onClick={(e) => onClick({ shift: e.shiftKey, toggle: e.metaKey || e.ctrlKey })}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-3 text-left sm:gap-3.5"
      >
        <span className="bg-sealwax/10 text-sealwax flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          {isImage ? <ImageIcon size={19} /> : <FileText size={19} />}
        </span>
        <span className="min-w-0 flex-1">
          {/* Der Dateiname bekommt die Zeile für sich. Der Sperr-Hinweis stand vorher neben ihm
              und hat ihn auf 360 px nach zehn Zeichen abgeschnitten. */}
          <span className="text-ink block truncate text-[15px] font-medium" title={file.fileName}>
            {file.fileName}
          </span>
          <span className="text-ink-faint mt-1 flex items-center gap-2 text-[13px]">
            <span className="bg-card text-ink-soft shrink-0 rounded px-1.5 py-0.5 text-xs font-medium">
              {PROPERTY_DOCUMENT_KIND_SHORT[file.kind]}
            </span>
            {file.locked && (
              <span className="text-sealwax inline-flex shrink-0 items-center gap-1 font-medium">
                <Lock size={12} /> Gesperrt
              </span>
            )}
            <span className="truncate">
              {file.locked
                ? `Aufbewahrung bis ${file.retentionUntil ? formatDate(file.retentionUntil) : 'unbefristet'}`
                : meta}
              {/* Auf schmalen Geräten hat die eigene Größenspalte den Namen erdrückt. */}
              <span className="tabular-nums sm:hidden">
                {(file.locked || meta) && ' · '}
                {size}
              </span>
            </span>
          </span>
        </span>
      </button>
      <span className="text-ink-faint hidden shrink-0 text-[13px] tabular-nums sm:inline">
        {size}
      </span>
      <AkteRowMenu items={buildFileMenuItems(file, actions)} />
    </li>
  );
}

function buildMetaLine(file: PropertyFileResponse): string {
  const parts: string[] = [];
  if (file.counterparty) parts.push(file.counterparty);
  if (file.issuedOn) parts.push(formatDate(file.issuedOn));
  if (file.amount != null && file.currency) parts.push(formatCurrency(file.amount, file.currency));
  return parts.join(' · ');
}
